import type { MapRef } from "react-map-gl";
import type { LayerSpec } from "../layers";
import { LayersControl } from "./LayersControl";
import { ListControl } from "./ListControl";
import { NavigationControl } from "./NavigationControl";
import { ControlGroup } from "./primitives/ControlGroup";
import { SearchControl } from "./SearchControl";
import type { MapControl } from "./types";
import { getControlPosition } from "./types";

interface RenderControlContext {
  mapRef: React.RefObject<MapRef | null>;
  /**
   * Whether reset zoom button should be shown
   */
  showResetButton?: boolean;
  /**
   * Layers array for dynamic layer control rendering
   */
  layers?: LayerSpec[];
}

/**
 * Render a single control based on its type
 */
export function renderControl(control: MapControl, index: number, context: RenderControlContext): React.ReactNode {
  const position = getControlPosition(control);
  // Use custom id if provided, otherwise generate from type and index
  const key = control.id ?? `${control.type}-${index}`;

  switch (control.type) {
    case "navigation": {
      // Only show reset zoom if BOTH the control enables it AND the context says the button should show
      // CRITICAL: Default to true if control.showResetZoom is undefined (to match NavigationControl default)
      // But still gate it on context.showResetButton
      const controlWantsResetZoom = control.showResetZoom !== false; // true if undefined or true
      const shouldShowResetZoom = controlWantsResetZoom && context.showResetButton;

      return (
        <NavigationControl
          key={key}
          mapRef={context.mapRef}
          position={position}
          showCompass={control.showCompass}
          showGeolocate={control.showGeolocate}
          showResetZoom={shouldShowResetZoom}
          onResetZoom={control.onResetZoom}
          onCompassClick={control.onCompassClick}
          onGeolocateClick={control.onGeolocateClick}
          style={control.style}
          className={control.className}
        />
      );
    }

    case "layers":
      return (
        <LayersControl
          key={key}
          position={position}
          currentMapType={control.currentMapType}
          onMapTypeChange={control.onMapTypeChange}
          layers={control.layers ?? context.layers}
          onLayerToggle={control.onLayerToggle}
          onClearAll={control.onClearAll}
          style={control.style}
          className={control.className}
        />
      );

    case "search":
      return (
        <SearchControl
          key={key}
          position={position}
          onSearch={control.onSearch}
          placeholder={control.placeholder}
          showSearchInput={control.showSearchInput}
          style={control.style}
          className={control.className}
        />
      );

    case "list":
      return (
        <ListControl
          key={key}
          position={position}
          onToggle={control.onToggle}
          isActive={control.isActive}
          style={control.style}
          className={control.className}
        />
      );

    case "custom":
      return <div key={key}>{control.render(context)}</div>;

    default:
      return null;
  }
}

/**
 * Group controls by position and render them
 *
 * When multiple controls share the same position, they are automatically
 * stacked together using ControlGroup.
 */
export function renderControlsByPosition(controls: MapControl[], context: RenderControlContext): React.ReactNode[] {
  // Group controls by position
  const controlsByPosition = controls.reduce(
    (acc, control, index) => {
      const position = getControlPosition(control);
      if (!acc[position]) {
        acc[position] = [];
      }
      acc[position].push({ control, index });
      return acc;
    },
    {} as Record<string, Array<{ control: MapControl; index: number }>>
  );

  // Render each position group
  const rendered: React.ReactNode[] = [];

  for (const [position, items] of Object.entries(controlsByPosition)) {
    // Single control - render directly
    if (items.length === 1) {
      rendered.push(renderControl(items[0].control, items[0].index, context));
      continue;
    }

    // Multiple controls - wrap in ControlGroup for auto-stacking. Each
    // inner control normally positions itself absolutely; when nested, that
    // pulls it out of the outer ControlGroup's flex flow and they collapse
    // on top of each other. Override style so the inner ControlBase falls
    // back to static positioning and participates in the flex column.
    const nestedStyle: React.CSSProperties = {
      position: "static",
      top: "auto",
      right: "auto",
      bottom: "auto",
      left: "auto",
    };
    rendered.push(
      <ControlGroup key={`group-${position}`} position={position as any} spacing="sm">
        {items.map(({ control, index }) => {
          const nested = { ...control, style: { ...control.style, ...nestedStyle } };
          return renderControl(nested, index, context);
        })}
      </ControlGroup>
    );
  }

  return rendered;
}
